import { useEffect, useRef, useState } from 'react';
import { Check, FileAudio, LoaderCircle, LogOut, Mail, Pencil, UserRound, Workflow, X } from 'lucide-react';
import type { AuthUser } from '@/auth/authUser';
import { OFFLINE_MODE } from '@/config/offline';

type UserProfileMenuProps = {
  user: AuthUser;
  loggingOut: boolean;
  error: string | null;
  onNicknameChange: (nickname: string) => void;
  onLogout: () => void;
  onPrecreate?: () => void;
  onIntroduction?: () => void;
};

const profileInitial = (nickname: string): string => (
  Array.from(nickname.trim()).slice(0, 2).join('').toLocaleUpperCase() || '?'
);

export function UserProfileMenu({
  user,
  loggingOut,
  error,
  onNicknameChange,
  onLogout,
  onPrecreate,
  onIntroduction,
}: UserProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState(user.nickname);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [open]);

  useEffect(() => {
    setNicknameDraft(user.nickname);
    setEditingNickname(false);
    setNicknameError(null);
  }, [user.nickname]);

  const saveNickname = () => {
    try {
      onNicknameChange(nicknameDraft);
      setEditingNickname(false);
      setNicknameError(null);
    } catch (caught) {
      setNicknameError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="プロフィールメニューを開く"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.nickname}
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-amber-400 bg-amber-400 text-xs font-bold text-white shadow-sm transition-colors hover:bg-amber-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
      >
        {user.avatarUrl
          ? <img src={user.avatarUrl} alt="" className="size-full object-cover" referrerPolicy="no-referrer" />
          : <span aria-hidden="true">{profileInitial(user.nickname)}</span>}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="アカウントメニュー"
          className="absolute right-0 top-[calc(100%+0.55rem)] z-[70] w-[min(20rem,calc(100vw-1rem))] overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-800 shadow-xl animate-[fade-drop_160ms_ease-out]"
        >
          <dl className="m-0 grid gap-3 px-4 py-3 text-xs">
            <div className="grid grid-cols-[1.1rem_minmax(0,1fr)] gap-x-2">
              <Mail aria-hidden="true" className="mt-0.5 text-slate-400" size={15} />
              <div className="min-w-0">
                <dt className="text-[0.65rem] font-semibold text-slate-500">メールアドレス</dt>
                <dd className="m-0 truncate font-medium" title={user.email ?? undefined}>
                  {user.email ?? '未設定'}
                </dd>
              </div>
            </div>
            <div className="grid grid-cols-[1.1rem_minmax(0,1fr)] gap-x-2">
              <UserRound aria-hidden="true" className="mt-0.5 text-slate-400" size={15} />
              <div className="min-w-0">
                <dt className="text-[0.65rem] font-semibold text-slate-500">ニックネーム</dt>
                <dd className="m-0">
                  {editingNickname ? (
                    <form
                      className="mt-1 flex items-center gap-1"
                      onSubmit={(event) => { event.preventDefault(); saveNickname(); }}
                    >
                      <input
                        autoFocus
                        value={nicknameDraft}
                        maxLength={100}
                        aria-label="ニックネーム"
                        onChange={(event) => setNicknameDraft(event.target.value)}
                        className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                      />
                      <button type="submit" aria-label="ニックネームを保存" className="grid size-7 place-items-center rounded-md text-emerald-700 hover:bg-emerald-50">
                        <Check aria-hidden="true" size={15} />
                      </button>
                      <button
                        type="button"
                        aria-label="ニックネーム編集をキャンセル"
                        onClick={() => { setNicknameDraft(user.nickname); setEditingNickname(false); setNicknameError(null); }}
                        className="grid size-7 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
                      >
                        <X aria-hidden="true" size={15} />
                      </button>
                    </form>
                  ) : (
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="min-w-0 flex-1 truncate font-medium" title={user.nickname}>{user.nickname}</span>
                      <button
                        type="button"
                        aria-label="ニックネームを編集"
                        onClick={() => setEditingNickname(true)}
                        className="grid size-7 shrink-0 place-items-center rounded-md text-slate-500 hover:bg-slate-100"
                      >
                        <Pencil aria-hidden="true" size={14} />
                      </button>
                    </div>
                  )}
                  {nicknameError && <span className="mt-1 block text-[0.68rem] text-red-700" role="alert">{nicknameError}</span>}
                </dd>
              </div>
            </div>
          </dl>
          {error && <p className="m-0 border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700" role="alert">{error}</p>}
          <div className="border-t border-slate-100 p-2">
            {OFFLINE_MODE && onIntroduction && <button type="button" role="menuitem"
              onClick={() => { setOpen(false); onIntroduction(); }}
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-xs font-semibold hover:bg-slate-100"
            ><Workflow aria-hidden="true" size={16} />ウィーゴ自己紹介</button>}
            {OFFLINE_MODE && onPrecreate && <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onPrecreate(); }}
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-xs font-semibold hover:bg-slate-100"
            ><FileAudio aria-hidden="true" size={16} />事前作成</button>}
            <button
              type="button"
              role="menuitem"
              disabled={loggingOut}
              onClick={onLogout}
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:cursor-wait disabled:opacity-50"
            >
              {loggingOut
                ? <LoaderCircle aria-hidden="true" className="animate-spin" size={16} />
                : <LogOut aria-hidden="true" size={16} />}
              {loggingOut ? '終了処理中' : OFFLINE_MODE ? '接続を終了して再読み込み' : 'ログアウト'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}